import { memo } from "react";
import { Box, Checkbox, IconButton, Typography, styled } from "@mui/material";
import { Pencil } from "lucide-react";

// Interfaces
import { getStaticUrlNews } from "./MeGenInterface";

// Types
import { GeneratedVideoItem } from "./types/MeGenTypes";

type CardGenerateVideoAssetItemProps = {
  item: GeneratedVideoItem;
  mediaVersion?: string;
  onEditClick: (item: GeneratedVideoItem) => void;
  // Edit mode props
  isEditMode?: boolean;
  isActive?: boolean;
  onActiveChange?: (id: string, active: boolean) => void;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
};

// ============================================================
// Shared Styles
// ============================================================

const AssetImage = styled("img")({ width: "100%", height: "10rem", objectFit: "cover" });

const AssetPlaceholder = styled(Box)({
  width: "100%", height: "10rem", backgroundColor: "#f1f5f9",
  display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8",
});

const AssetContent = styled(Box)({
  padding: "0.75rem", flex: 1, display: "flex", flexDirection: "column",
});

// ============================================================
// Normal Mode Styles
// ============================================================

const AssetCard = styled(Box)({
  border: "1px solid #e2e8f0", borderRadius: "0.5rem", overflow: "hidden",
  display: "flex", flexDirection: "column", position: "relative",
});

const EditButton = styled(IconButton)({
  position: "absolute", top: "0.5rem", right: "0.5rem", padding: "0.375rem",
  backgroundColor: "rgba(255, 255, 255, 0.9)", color: "#475569",
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
  "&:hover": { backgroundColor: "white" },
});

// ============================================================
// Edit Mode Styles
// ============================================================

const EditModeCard = styled(Box)<{ isActive: boolean }>(({ isActive }) => ({
  border: `2px solid ${isActive ? "#f9a8d4" : "#e2e8f0"}`,
  borderRadius: "0.5rem",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  backgroundColor: isActive ? "#fff0f6" : "#f8fafc",
  opacity: isActive ? 1 : 0.5,
  transition: "all 0.15s ease",
  cursor: "default",
  userSelect: "none",
}));

const EditModeOverlay = styled(Box)({
  position: "absolute",
  top: "0.5rem",
  right: "0.5rem",
  zIndex: 2,
  backgroundColor: "rgba(255, 255, 255, 0.92)",
  borderRadius: "0.375rem",
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  padding: "0.125rem",
});

const DragHandleOverlay = styled(Box)({
  position: "absolute",
  top: "0.5rem",
  left: "0.5rem",
  zIndex: 2,
  backgroundColor: "rgba(255, 255, 255, 0.85)",
  borderRadius: "0.25rem",
  display: "flex",
  alignItems: "center",
  padding: "0.25rem",
  color: "#ec4899",
  cursor: "grab",
  "&:active": { cursor: "grabbing" },
});

// ============================================================
// Main Component
// ============================================================

const CardGenerateVideoAssetItem = (props: CardGenerateVideoAssetItemProps) => {
  const isEditMode = props.isEditMode ?? false;
  const isActive = props.isActive ?? true;

  const rawImageUrl = getStaticUrlNews(props.item.image_path) || "";
  const rawAudioUrl = getStaticUrlNews(props.item.audio_path) || "";

  const imageUrl = rawImageUrl && props.mediaVersion ? `${rawImageUrl}?v=${props.mediaVersion}` : rawImageUrl;
  const audioUrl = rawAudioUrl && props.mediaVersion ? `${rawAudioUrl}?v=${props.mediaVersion}` : rawAudioUrl;

  // ---- Edit Mode ----
  if (isEditMode) {
    return (
      <EditModeCard id={`megen-asset-props.item-${props.item.id}-container`} isActive={isActive}>
        {/* Drag Handle (top-left) */}
        <DragHandleOverlay {...props.dragHandleProps}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="5" cy="4" r="1.5" />
            <circle cx="11" cy="4" r="1.5" />
            <circle cx="5" cy="8" r="1.5" />
            <circle cx="11" cy="8" r="1.5" />
            <circle cx="5" cy="12" r="1.5" />
            <circle cx="11" cy="12" r="1.5" />
          </svg>
        </DragHandleOverlay>

        {/* Checkbox (top-right) */}
        <EditModeOverlay>
          <Checkbox
            checked={isActive}
            onChange={(e) => props.onActiveChange?.(String(props.item.id), e.target.checked)}
            size="small"
            sx={{
              padding: "0.125rem",
              color: "#f9a8d4",
              "&.Mui-checked": { color: "#ec4899" },
            }}
          />
        </EditModeOverlay>

        {/* Image */}
        {imageUrl ? (
          <AssetImage src={imageUrl} alt={props.item.headline || ""} />
        ) : (
          <AssetPlaceholder>No Image</AssetPlaceholder>
        )}

        {/* Content: headline only, no audio */}
        <AssetContent>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: "0.875rem",
              color: isActive ? "#1e293b" : "#94a3b8",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              transition: "color 0.15s ease",
            }}
            title={props.item.headline || ""}
          >
            {props.item.headline || props.item.id}
          </Typography>
        </AssetContent>
      </EditModeCard>
    );
  }

  // ---- Normal Mode ----
  return (
    <AssetCard id={`megen-asset-props.item-${props.item.id}-container`}>
      <EditButton id={`megen-asset-props.item-${props.item.id}-edit-btn`} size="small" onClick={() => props.onEditClick(props.item)} title="Edit Item">
        <Pencil size={14} />
      </EditButton>
      {imageUrl ? (
        <AssetImage src={imageUrl} alt={props.item.headline || ""} />
      ) : (
        <AssetPlaceholder>No Image</AssetPlaceholder>
      )}
      <AssetContent>
        <Typography
          sx={{
            fontWeight: 500, fontSize: "0.875rem", color: "#1e293b", mb: 1,
            overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box",
            WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          }}
          title={props.item.headline || ""}
        >
          {props.item.headline || props.item.id}
        </Typography>
        {audioUrl && <audio controls src={audioUrl} style={{ width: "100%", height: "2rem" }} />}
      </AssetContent>
    </AssetCard>
  );
};

CardGenerateVideoAssetItem.displayName = "CardGenerateVideoAssetItem";

export default memo(CardGenerateVideoAssetItem);